import { useCallback, useState, type FormEvent, type JSX } from 'react';

import {
  ApiError,
  shaktiApi,
  type GraphImpactResponse,
  type GraphImpactedNode,
} from '../api/client';

/**
 * glistening-zebra Phase F1 — Axon impact analysis route.
 *
 * The dashboard already covers Execution (Command Center), Diff
 * (Approvals), Context (Dependencies + Lifecycle). The one Tauri
 * panel intent that wasn't surfaced anywhere in the dashboard was
 * blast-radius / impact analysis driven by `routes.rs::graph_impact`.
 * This route fills that gap so Phase F2 (Tauri shell loading the
 * dashboard) doesn't lose a panel during consolidation.
 *
 * The user types a fully-qualified node key (matching the Axon
 * `node_key` schema, e.g. `crates::shakti-server::src::routes.rs::version`),
 * picks a BFS depth (1–5; default 3), and the route renders:
 *   - a normalised risk score (0–10, capped server-side)
 *   - the count of impacted files
 *   - a depth-grouped list of impacted symbols
 *
 * Embedded inside Tauri / VS Code webviews this is the same view —
 * the only difference is what URL the host fronts. No special-case
 * code; the React tree is the contract.
 */
export function ImpactRoute(): JSX.Element {
  const [nodeKey, setNodeKey] = useState('');
  const [depth, setDepth] = useState<number>(3);
  const [result, setResult] = useState<GraphImpactResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const trimmed = nodeKey.trim();
      if (!trimmed) {
        setError('Enter a node key (e.g. crates::shakti-server::src::routes.rs::version)');
        return;
      }
      setError(null);
      setLoading(true);
      const controller = new AbortController();
      try {
        const resp = await shaktiApi.graphImpact(
          { node_key: trimmed, depth },
          { signal: controller.signal },
        );
        setResult(resp);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setResult(null);
        setError(
          err instanceof ApiError ? err.body?.error ?? err.message : 'Failed to load impact',
        );
      } finally {
        setLoading(false);
      }
    },
    [nodeKey, depth],
  );

  return (
    <section aria-labelledby="impact-heading">
      <header className="mb-6">
        <p className="text-2xs uppercase tracking-wider text-fg-muted">Axon</p>
        <h1 id="impact-heading" className="mt-1 text-2xl font-semibold">
          Impact analysis
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-fg-secondary">
          Explore the BFS-reachable blast radius of a code symbol. Pick any node key from the
          Axon graph; the depth control limits how far the traversal walks before stopping.
          Risk score is normalised to 0–10, where 10 means a change touches enough of the
          codebase to require human review at every checkpoint.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="mb-6 grid gap-3 rounded-md border border-border-subtle bg-bg-surface p-4 md:grid-cols-[1fr_auto_auto]"
      >
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          <span>Node key</span>
          <input
            type="text"
            value={nodeKey}
            onChange={(e) => setNodeKey(e.target.value)}
            placeholder="crates::shakti-server::src::routes.rs::version"
            className="rounded-sm border border-border-subtle bg-bg-surface-elevated px-3 py-2 font-mono text-xs text-fg-primary"
            data-testid="impact-node-key-input"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          <span>Depth</span>
          <select
            value={depth}
            onChange={(e) => setDepth(Number(e.target.value))}
            className="rounded-sm border border-border-subtle bg-bg-surface-elevated px-2 py-2 text-xs text-fg-primary"
            data-testid="impact-depth-select"
          >
            {[1, 2, 3, 4, 5].map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={loading}
          className="self-end rounded-sm bg-accent px-4 py-2 text-xs font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
          data-testid="impact-submit"
        >
          {loading ? 'Analysing…' : 'Analyse'}
        </button>
      </form>

      {error && (
        <div
          role="alert"
          className="mb-6 rounded-md border border-warning bg-bg-surface p-4 text-sm text-warning"
          data-testid="impact-error"
        >
          {error}
        </div>
      )}

      {result && <ImpactResultView result={result} />}
    </section>
  );
}

function ImpactResultView({ result }: { result: GraphImpactResponse }): JSX.Element {
  const grouped = groupByDepth(result.impacted_nodes);
  const depths = Array.from(grouped.keys()).sort((a, b) => a - b);
  const riskColor = riskTone(result.risk_score);

  return (
    <div className="space-y-6" data-testid="impact-result">
      <div className="grid gap-3 md:grid-cols-3">
        <Stat
          label="Risk score"
          value={result.risk_score.toFixed(1)}
          hint="0–10 (10 = full review)"
          tone={riskColor}
        />
        <Stat
          label="Impacted symbols"
          value={String(result.impacted_nodes.length)}
          hint="BFS-reachable from the root"
        />
        <Stat
          label="Impacted files"
          value={String(result.impacted_files.length)}
          hint="Distinct files touched"
        />
      </div>

      <section aria-labelledby="impact-files-heading">
        <h2 id="impact-files-heading" className="mb-2 text-sm font-semibold">
          Impacted files ({result.impacted_files.length})
        </h2>
        {result.impacted_files.length === 0 ? (
          <p className="text-xs text-fg-muted">No file metadata on the reachable nodes.</p>
        ) : (
          <ul className="space-y-1">
            {result.impacted_files.map((f) => (
              <li
                key={f}
                className="rounded-sm border border-border-subtle bg-bg-surface px-3 py-1.5 font-mono text-xs text-fg-secondary"
              >
                {f}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="impact-symbols-heading">
        <h2 id="impact-symbols-heading" className="mb-2 text-sm font-semibold">
          Impacted symbols by depth
        </h2>
        {depths.length === 0 ? (
          <p className="text-xs text-fg-muted">No symbols reachable.</p>
        ) : (
          <div className="space-y-3">
            {depths.map((d) => {
              const nodes = grouped.get(d) ?? [];
              return (
                <details
                  key={d}
                  className="rounded-md border border-border-subtle bg-bg-surface"
                  open={d <= 1}
                >
                  <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-fg-primary">
                    Depth {d} · {nodes.length}{' '}
                    <span className="font-normal text-fg-muted">
                      {nodes.length === 1 ? 'symbol' : 'symbols'}
                    </span>
                  </summary>
                  <ul className="border-t border-border-subtle">
                    {nodes.map((n) => (
                      <li
                        key={n.id}
                        className="flex items-baseline gap-3 border-b border-border-subtle px-3 py-1.5 text-xs last:border-b-0"
                      >
                        <span className="font-mono text-fg-primary">{n.name}</span>
                        <span className="font-mono text-2xs text-fg-muted">{n.node_key}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'low' | 'medium' | 'high';
}): JSX.Element {
  const toneClass =
    tone === 'high'
      ? 'border-warning text-warning'
      : tone === 'medium'
        ? 'border-accent text-accent'
        : 'border-border-subtle text-fg-primary';
  return (
    <div className={`rounded-md border bg-bg-surface p-4 ${toneClass}`}>
      <p className="text-2xs uppercase tracking-wider text-fg-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
      {hint && <p className="mt-1 text-2xs text-fg-muted">{hint}</p>}
    </div>
  );
}

function groupByDepth(nodes: GraphImpactedNode[]): Map<number, GraphImpactedNode[]> {
  const out = new Map<number, GraphImpactedNode[]>();
  for (const n of nodes) {
    const bucket = out.get(n.depth) ?? [];
    bucket.push(n);
    out.set(n.depth, bucket);
  }
  // Sort each depth's symbols alphabetically by node_key for
  // stable diffing across re-fetches.
  for (const bucket of out.values()) {
    bucket.sort((a, b) => a.node_key.localeCompare(b.node_key));
  }
  return out;
}

function riskTone(score: number): 'low' | 'medium' | 'high' {
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
}
